import { useEffect, useRef } from "react"
import { useSearchParams } from "react-router"
import { useResetChatHistoryTo } from "@/modules/chats/hooks/useChatHistory"
import { useChatSummaryPrefill } from "@/modules/chats/hooks/useChatSummaryPrefill"
import { useGreetingPrefill } from "@/modules/chats/hooks/useGreetingPrefill"
import { HistoryMessage } from "@/modules/chats/lib/chat-history-reducer"
import { determineInputPrefillKind } from "@/modules/chats/lib/determine-input-prefill"
import { CHAT_TAKEN_PARAM } from "@/modules/chats/lib/routing-helpers"
import { useUnsentMessageContext } from "@/modules/chats/stores/useUnsentMessageStore"
import { UnsentChatMessage } from "@/modules/chats/types/chat-message"
import { ChatThread } from "@/modules/chats/types/chat-thread"

const buildUnsentDraftHistory = (unsentMessage: UnsentChatMessage): HistoryMessage[] => [
  {
    content: unsentMessage.sendContent,
    contentBeforeAiFeature: unsentMessage.contentBeforeAiFeature ?? undefined,
    aiFeatures: unsentMessage.aiFeatures ?? [],
  },
]

export const useChatInputPrefill = (selectedThread: ChatThread): void => {
  const getUnsentMessage = useUnsentMessageContext((state) => state.getUnsentMessage)
  const [searchParams, setSearchParams] = useSearchParams()
  const resetHistoryTo = useResetChatHistoryTo()
  const applyGreeting = useGreetingPrefill()
  const isEffectCancelledRef = useRef(false)
  const applySummary = useChatSummaryPrefill(selectedThread, isEffectCancelledRef)

  const hasPrefilledRef = useRef(false)
  const wasChatJustTakenRef = useRef(searchParams.get(CHAT_TAKEN_PARAM) === "true")
  const wasGreetingReceivedAtTakeRef = useRef(selectedThread.greetingMessageReceived)
  const unsentMessageAtTakeRef = useRef(getUnsentMessage(selectedThread.id))

  useEffect(() => {
    if (searchParams.get(CHAT_TAKEN_PARAM) !== "true") {
      return
    }

    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams)
        nextParams.delete(CHAT_TAKEN_PARAM)
        return nextParams
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams])

  useEffect(() => {
    isEffectCancelledRef.current = false
    if (hasPrefilledRef.current) {
      return
    }
    hasPrefilledRef.current = true

    const unsentMessage = unsentMessageAtTakeRef.current
    const hasUnsentDraft = !!unsentMessage?.sendContent
    const prefillKind = determineInputPrefillKind({
      hasUnsentDraft,
      wasGreetingReceivedAtTake: wasGreetingReceivedAtTakeRef.current,
      wasChatJustTaken: wasChatJustTakenRef.current,
    })

    switch (prefillKind) {
      case "unsentDraft":
        if (unsentMessage) {
          resetHistoryTo(buildUnsentDraftHistory(unsentMessage))
        }
        break
      case "greeting":
        applyGreeting(selectedThread)
        break
      case "summary":
        void applySummary()
        break
      case "none":
        break
    }

    return () => {
      isEffectCancelledRef.current = true
    }
  }, [selectedThread, resetHistoryTo, applyGreeting, applySummary])
}
